
import { useState, useCallback, useEffect } from 'react';
import { X, GitFork, Check, AlertTriangle } from 'lucide-react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { toast } from 'sonner';

interface DynamicNextDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    nodeName: string;
    currentNextPath: string;
    targets: string[];
    onSave: (params: { nextPath: string }) => void;
}

export default function DynamicNextDrawer({
    isOpen,
    onClose,
    nodeName,
    currentNextPath,
    targets,
    onSave,
}: DynamicNextDrawerProps) {
    const [nextPath, setNextPath] = useState(currentNextPath);

    useEffect(() => {
        setNextPath(currentNextPath);
    }, [currentNextPath, isOpen]);

    const isValid = nextPath.startsWith('$.') || nextPath.startsWith('$$.');

    const handleSave = useCallback(() => {
        if (!isValid) {
            toast.error('Invalid JSONPath', {
                description: 'Dynamic Next must start with "$." or "$$." (e.g., $.result.next_state)',
            });
            return;
        }
        onSave({ nextPath });
        onClose();
        toast.success('Dynamic routing configured', {
            description: `Next = ${nextPath}`,
        });
    }, [nextPath, isValid, onSave, onClose]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && isValid) {
                handleSave();
            }
        },
        [handleSave, isValid]
    );

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                        {/* Header */}
                        <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-100">
                                        <GitFork className="h-5 w-5 text-cyan-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl font-semibold text-slate-900">
                                            Dynamic Routing
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            Configure JSONPath expression for <span className="font-mono font-medium text-slate-700">{nodeName}</span>
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onClose();
                                    }}
                                    className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="px-6 py-6 space-y-6">
                            {/* JSONPath Input */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-700">
                                    Next (JSONPath Expression)
                                </label>
                                <p className="text-xs text-slate-500">
                                    At runtime, this path is resolved against the state output to determine which target state to transition to.
                                </p>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={nextPath}
                                        onChange={(e) => setNextPath(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="$.result.next_state"
                                        className={`w-full rounded-lg border-2 px-4 py-3 font-mono text-sm transition-colors outline-none ${
                                            nextPath.length === 0
                                                ? 'border-slate-200 focus:border-cyan-500'
                                                : isValid
                                                    ? 'border-cyan-500 bg-cyan-50/50'
                                                    : 'border-red-400 bg-red-50/50'
                                        }`}
                                        autoFocus
                                    />
                                    {nextPath.length > 0 && (
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                            {isValid ? (
                                                <Check size={18} className="text-cyan-600" />
                                            ) : (
                                                <AlertTriangle size={18} className="text-red-500" />
                                            )}
                                        </div>
                                    )}
                                </div>
                                {nextPath.length > 0 && !isValid && (
                                    <p className="text-xs text-red-600">
                                        Must start with <code className="bg-red-100 px-1 rounded">$.</code> (state data) or <code className="bg-red-100 px-1 rounded">$$.</code> (system context)
                                    </p>
                                )}
                            </div>

                            {/* Target States */}
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-slate-700">
                                    Possible Targets ({targets.length})
                                </label>
                                <p className="text-xs text-slate-500">
                                    The resolved value must match one of these connected state names.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {targets.map((target) => (
                                        <span
                                            key={target}
                                            className="inline-flex items-center gap-1.5 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1.5 text-xs font-medium text-cyan-800"
                                        >
                                            <div className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                                            {target}
                                        </span>
                                    ))}
                                </div>
                            </div>

                            {/* Help */}
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                                <p className="text-xs font-medium text-slate-600 mb-2">Examples</p>
                                <div className="space-y-1.5 font-mono text-xs text-slate-500">
                                    <p><span className="text-cyan-700">$.result.next_state</span> — resolve from task output</p>
                                    <p><span className="text-cyan-700">$.decision.delegate_to</span> — delegation routing</p>
                                    <p><span className="text-cyan-700">$$.Context.next_state</span> — resolve from system context</p>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end items-center gap-3">
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onClose();
                                }}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 hover:shadow-md"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleSave();
                                }}
                                disabled={!isValid}
                                className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium shadow-sm transition-all ${
                                    isValid
                                        ? 'bg-cyan-600 text-white hover:bg-cyan-700 hover:shadow-md'
                                        : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                }`}
                            >
                                <Check size={16} />
                                Save
                            </button>
                        </div>
        </SlideDrawer>
    );
}
